import { cn } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Textarea from 'components/shared/Textarea/Textarea';

import { IMPORT_TYPE } from '../lib/lib';

const ImportTypeSelector = ({ initialImportForm, handleImportTypeSelection, handleChange }) => (
  <div className="flex w-full justify-center">
    <div className="w-full text-left lg:px-4 md:px-4">
      <div className="flex flex-col gap-4">
        <div>
          <Input
            data-qa="patients-number"
            label="Number of patients requested"
            type="number"
            name="numberOfPatients"
            required
            showNumberControls
            value={initialImportForm?.numberOfPatients}
            onChange={(event) => handleChange(event.target.name, event.target.value)}
          />
        </div>
        <div>
          <Textarea
            placeholder="Enter a note"
            textareaClassName="h-20"
            label="General note (optional)"
            value={initialImportForm?.generalNote}
            name="generalNote"
            onChange={(event) => handleChange(event.target.name, event.target.value)}
          />
        </div>
        <div className="text-sm">
          <span className="flex items-center gap-2 text-warning-500">
            <Icon icon="shield-warning-mono" color="warning" />
            Important reminder
          </span>
          <span className="italic">
            Whether you are importing via CSV or a full software export, the accuracy of your data
            is your responsibility. Incorrect or incomplete information can affect patient care,
            billing, and compliance. Please verify your files before uploading to ensure a smooth
            transition.
          </span>
        </div>
        <div className="pb-6">
          <p className="mb-2">Select between CSV or software export</p>
          <div className="full flex gap-4 sm:flex-col">
            <div
              onClick={() => handleImportTypeSelection(IMPORT_TYPE.CSV_IMPORT)}
              className="relative flex w-full cursor-pointer flex-col items-center justify-center gap-4 rounded-lg border border-solid border-primary-100 bg-primary-25 px-4 py-8">
              <div
                className={cn(
                  'absolute left-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border border-solid border-neutral-300 bg-white',
                  initialImportForm.selectedType === IMPORT_TYPE.CSV_IMPORT && 'border-primary-300'
                )}>
                {initialImportForm.selectedType === IMPORT_TYPE.CSV_IMPORT && (
                  <div className="h-2 w-2 rounded-full bg-primary-300"></div>
                )}
              </div>
              <Icon icon="file-vector-grid" />
              <div className="flex flex-col gap-2">
                <p className="mx-auto w-fit font-semibold text-primary-600">CSV file</p>
                <p className="mx-auto w-full text-justify text-sm text-neutral-700">
                  If you have exported a spreadsheet from your prior software—or if you've been
                  provided data (especially patient demographics) in CSV format—please use this
                  option. CSV files are straightforward to import and allow for faster onboarding,
                  but they require close attention to formatting and accuracy.
                </p>
              </div>
            </div>
            <div
              onClick={() => handleImportTypeSelection(IMPORT_TYPE.SOFTWARE_EXPORT)}
              className="relative flex w-full cursor-pointer flex-col items-center justify-center gap-4 rounded-lg border border-solid border-primary-100 bg-primary-25 px-4 py-8">
              <div className="absolute left-0 top-0">
                <div
                  className={cn(
                    'absolute left-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border border-solid border-neutral-300 bg-white',
                    initialImportForm.selectedType === IMPORT_TYPE.SOFTWARE_EXPORT &&
                      'border-primary-300'
                  )}>
                  {initialImportForm.selectedType === IMPORT_TYPE.SOFTWARE_EXPORT && (
                    <div className="h-2 w-2 rounded-full bg-primary-300"></div>
                  )}
                </div>
              </div>
              <Icon icon="file-vector-zip" />
              <div className="flex flex-col gap-2">
                <p className="mx-auto w-fit font-semibold text-primary-600">Software export</p>
                <p className="mx-auto w-full text-justify text-sm text-neutral-700">
                  If your legacy software vendor has created a bundled export and shared the data
                  with you, this is the preferred method for advanced imports. Our system can map
                  these files to most major practice management and EHR systems currently on the
                  market. Please note that under the Cures Act, you are entitled to receive and use
                  this data, provided it is confirmed that you own it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ImportTypeSelector;
